import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Grid, Select, Skeleton, Typography, message } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, ProfileOutlined } from '@ant-design/icons';
import {
  checkScenarioVersion,
  getScenarioReview,
  getScenarioVersion,
  saveScenarioVersion,
  type EvaluationScenario,
  type ScenarioReviewCase,
  type ScenarioVersionCheck,
  type ScenarioVersionForEdit,
} from '@/api/scenarioVersions';
import { useHoldUnsavedWork } from '@/components/practiceSwitch';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { PART_WORDS } from '../scenarioWords';
import { surface } from '../review/reviewParts';
import { sameVersion } from './scenarioEdits';
import { ActorsSection, ChecksSection, ContextSection, ExpectationsSection, ProvenanceSection } from './editorSections';
import { ControlsSection, HazardSourcesSection, SiblingsSection, ToolAnswersSection } from './caseBasisSections';
import { ScenarioMergeDrawer, StandaloneVariantDrawer } from './editorDrawers';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// AdvancedScenarioEditorDrawer — Scenario Intelligence screen 10 (plan §5 "advanced editor"; contract §3, §5, §6).
//
// A wide drawer over one version, in sections: Context, Actors & timeline, Expectations, Checks & evidence, and since
// contract version 3 (2026-11-02) Controls, Tool answers, Hazard & sources and Siblings, then Provenance. No JSON and no runner vocabulary; the server checks every edit (Version/Check) and says in words why a
// version cannot run. Protocol context is optional and removing it never hides an obligation: each check runs, is Not
// applicable by a reviewed rule, or blocks the version ("Create standalone variant" keeps the original).
// A save is checked against the hash the version was opened at; a newer save in the meantime is a conflict, merged
// Original / Yours / Current in a stacked drawer, and the merged result is saved only if it compiles.
// A file-managed scenario opens read-only here until the library migration moves it into the database.
// Opened from a scenario's "Edit details" on the Scenarios page, and from the review's "Open the whole scenario".
// ─────────────────────────────────────────────────────────────────────────────

type SectionKey = 'context' | 'actors' | 'expectations' | 'checks' | 'controls' | 'doubles' | 'basis' | 'siblings' | 'provenance';
const SECTIONS: Array<{ key: SectionKey; label: string }> = [
  { key: 'context', label: 'Context' },
  { key: 'actors', label: 'Actors & timeline' },
  { key: 'expectations', label: 'Expectations' },
  { key: 'checks', label: 'Checks & evidence' },
  { key: 'controls', label: 'Controls' },
  { key: 'doubles', label: 'Tool answers' },
  { key: 'basis', label: 'Hazard & sources' },
  { key: 'siblings', label: 'Siblings' },
  { key: 'provenance', label: 'Provenance' },
];

interface Conflict { current: EvaluationScenario; hash: string; savedBy: number; savedAtUtc?: string | null }

export const AdvancedScenarioEditorDrawer = observer(function AdvancedScenarioEditorDrawer({
  open,
  onClose,
  scenarioId,
  onSaved,
  onOpenScenario,
}: {
  open: boolean;
  onClose: () => void;
  scenarioId: string | null;
  onSaved?: () => void;
  /** Opens another scenario in this editor (the standalone variant once it exists). */
  onOpenScenario?: (scenarioId: string) => void;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const narrow = !Grid.useBreakpoint().md;
  const s = surface(isDark);

  const [view, setView] = useState<ScenarioVersionForEdit | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [original, setOriginal] = useState<EvaluationScenario | null>(null);
  const [draft, setDraft] = useState<EvaluationScenario | null>(null);
  const [baseHash, setBaseHash] = useState('');
  const [history, setHistory] = useState<ScenarioReviewCase | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [check, setCheck] = useState<ScenarioVersionCheck | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState<string[] | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [variantOpen, setVariantOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const loadSeq = useRef(0);
  const checkSeq = useRef(0);
  const scroller = useRef<HTMLDivElement>(null);

  const dirty = !!draft && !!original && !sameVersion(draft, original);
  const editable = !!view?.editable;
  useHoldUnsavedWork(open && dirty, `scenario ${scenarioId ?? ''}`);

  useEffect(() => {
    if (!open || !scenarioId) return;
    const mine = ++loadSeq.current;
    setView(null);
    setLoadError(null);
    setRefused(null);
    setConflict(null);
    setHistory(null);
    setHistoryError(null);
    getScenarioVersion(scenarioId)
      .then((v) => {
        if (mine !== loadSeq.current) return;
        setView(v);
        setOriginal(v.version);
        setDraft(v.version);
        setBaseHash(v.baseHash);
        setCheck(v.check ?? null);
        setCheckedAt(new Date());
        return getScenarioReview(v.agentId, v.scenarioId)
          .then((h) => mine === loadSeq.current && setHistory(h))
          .catch((e: Error) => mine === loadSeq.current && setHistoryError(e.message));
      })
      .catch((e: Error) => mine === loadSeq.current && setLoadError(e.message));
  }, [open, scenarioId, reload]);

  // Every edit is checked by the server, a moment after typing stops.
  useEffect(() => {
    if (!draft || !editable || !dirty) return;
    const mine = ++checkSeq.current;
    const t = window.setTimeout(() => {
      checkScenarioVersion(draft)
        .then((c) => {
          if (mine !== checkSeq.current) return;
          setCheck(c);
          setCheckedAt(new Date());
        })
        .catch(() => { /* the save checks again; a failed preview check is not a refusal */ });
    }, 450);
    return () => window.clearTimeout(t);
  }, [draft, editable, dirty]);

  const goTo = useCallback((key: string) => {
    document.getElementById(key)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setRefused(null);
    try {
      const r = await saveScenarioVersion(draft, baseHash);
      if (r.outcome === 'Saved' || r.outcome === 'Unchanged') {
        void message.success(r.outcome === 'Saved' ? 'Saved as a new version. It needs its own approval.' : 'Nothing changed, so no new version was made.');
        onSaved?.();
        setReload((n) => n + 1);
      } else if (r.outcome === 'Conflict' && r.current && r.hash) {
        setConflict({ current: r.current, hash: r.hash, savedBy: r.currentSavedBy ?? 0, savedAtUtc: r.currentSavedAtUtc });
      } else {
        setRefused(r.problems.length ? r.problems : ['The version was not saved.']);
      }
    } catch (e) {
      setRefused([(e as Error).message]);
    } finally {
      setSaving(false);
      // A refusal or a conflict is said at the top of the drawer; bring it into view wherever the person was editing.
      scroller.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const applyMerged = (merged: EvaluationScenario) => {
    if (!conflict) return;
    setOriginal(conflict.current);
    setBaseHash(conflict.hash);
    setDraft(merged);
    setConflict(null);
    setMergeOpen(false);
    void message.info('Merged. Check the result, then save it: the merged version must compile.');
  };

  const currentNumber = history?.item.current.number;
  const savedByName = conflict
    ? history?.versions.find((v) => v.authorUserId === conflict.savedBy)?.authorName ?? (conflict.savedBy ? `user #${conflict.savedBy}` : 'Someone')
    : '';
  const drafts = draft?.checks.filter((c) => c.requirementRef?.trim().toLowerCase().startsWith('draft:')).length ?? 0;
  const weak = draft?.checks.filter((c) => c.strength === 'Weak').length ?? 0;
  const blocked = draft?.checks.some((c) => draft.world.infusionProtocolId <= 0 && c.applicability?.withoutProtocol === 'NeedsProtocol');
  const problems = check?.problems ?? [];
  const misbehaving = (check?.controls ?? []).filter((v) => !v.behaves).length;

  const nav = (
    <nav aria-label="Sections" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {SECTIONS.map((sec) => {
        const note =
          sec.key === 'context' ? (blocked ? <StatusPill tone="red">blocked</StatusPill> : <CheckCircleOutlined style={{ color: colors.green[600] }} />)
            : sec.key === 'expectations' && drafts > 0 ? <StatusPill tone="amber">{drafts} draft</StatusPill>
              : sec.key === 'checks' && weak > 0 ? <StatusPill tone="amber">{weak} weak</StatusPill>
                : sec.key === 'controls' && misbehaving > 0 ? <StatusPill tone="amber">{misbehaving} warn</StatusPill>
                  : sec.key === 'basis' && (draft?.sources ?? []).length === 0 ? <StatusPill tone="slate">none</StatusPill>
                    : sec.key === 'actors' ? <CheckCircleOutlined style={{ color: colors.green[600] }} /> : null;
        return (
          <button key={sec.key} type="button" onClick={() => goTo(`section-${sec.key}`)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '8px 10px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', color: s.text, textAlign: 'left', fontSize: 13.5 }}>
            {sec.label} {note}
          </button>
        );
      })}
      <CompileStatus check={check} checkedAt={checkedAt} drafts={drafts} isDark={isDark} />
    </nav>
  );

  const title = (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <ProfileOutlined style={{ color: colors.violet[600] }} />
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13.5, wordBreak: 'break-all' }}>{scenarioId}</span>
        {dirty && <StatusPill tone="amber">Unsaved changes</StatusPill>}
        {view && !editable && <StatusPill tone="slate">Read only</StatusPill>}
      </div>
      {view && (
        <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
          Scenario for {view.agentName ?? 'the agent'} #{view.agentId}
          {currentNumber ? ` · editing version ${currentNumber}${editable ? `, saves as version ${currentNumber + 1}` : ''}` : ''} · platform-owned, results stay with the practice that ran it
        </Text>
      )}
    </div>
  );

  const changed = check?.changedParts ?? [];
  const footer = view && editable && draft ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {dirty ? `${changed.length ? changed.map((p) => PART_WORDS[p]).join(', ') : 'Your edits'} changed since version ${currentNumber ?? '?'}. Saving makes a new, unapproved version.` : 'No changes.'}
      </Text>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button disabled={!dirty || saving} onClick={() => { setDraft(original); setRefused(null); }}>Discard changes</Button>
        <Button type="primary" loading={saving} disabled={!dirty || !!conflict || problems.length > 0} onClick={() => void save()}>
          Save version {(currentNumber ?? 0) + 1}
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(1120px, 100vw)"
      title={title}
      footer={footer}
      styles={{ body: { padding: 0, background: s.page, display: 'flex', minHeight: 0 } }}
    >
      {loadError ? (
        <div style={{ padding: 20, flex: 1 }}>
          <Alert type="error" showIcon title="Could not open this scenario" description={`The server said: “${loadError}”`}
            action={<Button size="small" onClick={() => setReload((n) => n + 1)}>Retry</Button>} />
        </div>
      ) : !view || !draft || !original ? (
        <div style={{ padding: 20, flex: 1 }} aria-busy="true">
          {view && !view.version ? (
            <Alert type="error" showIcon title="This version cannot be opened" description={view.notEditableReason ?? 'Its body could not be read.'} />
          ) : (
            <>
              <Text strong style={{ display: 'block' }}>Opening {scenarioId}</Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12.5 }}>Reading the scenario, its checks and every version with who wrote and approved it.</Text>
              <Skeleton active paragraph={{ rows: 10 }} />
            </>
          )}
        </div>
      ) : (
        <>
          {!narrow && <div style={{ width: 220, flexShrink: 0, padding: 12, borderRight: `1px solid ${s.line}`, background: s.card, overflowY: 'auto' }}>{nav}</div>}
          <div ref={scroller} style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: narrow ? 12 : 20, display: 'flex', flexDirection: 'column', gap: 24 }}>
            {narrow && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Select aria-label="Section" placeholder="Section" onChange={(k: string) => goTo(`section-${k}`)} options={SECTIONS.map((x) => ({ value: x.key, label: x.label }))} />
                <CompileStatus check={check} checkedAt={checkedAt} drafts={drafts} isDark={isDark} />
              </div>
            )}
            {!editable && <Alert type="info" showIcon title="Read only" description={view.notEditableReason} />}
            {conflict && (
              <Alert
                type="warning"
                showIcon
                title={`Version ${(currentNumber ?? 0) + 1} was saved by ${savedByName} while you edited version ${currentNumber ?? '?'}`}
                description={
                  <>
                    <div>Your changes are kept here. Merge them with theirs, choosing Original, Yours or Current wherever you both changed the same part; then save the merged version. Saving over theirs without merging is not offered.</div>
                    <Button style={{ marginTop: 8 }} size="small" type="primary" onClick={() => setMergeOpen(true)}>Open the three-way merge</Button>
                  </>
                }
              />
            )}
            {refused && (
              <Alert type="error" showIcon closable onClose={() => setRefused(null)}
                title={`Not saved: ${refused.length} thing${refused.length === 1 ? '' : 's'} to fix`}
                description={<Problems problems={refused} onGo={goTo} />} />
            )}
            {!refused && dirty && problems.length > 0 && (
              <Alert type="error" showIcon
                title={`Can’t save yet: ${problems.length} thing${problems.length === 1 ? '' : 's'} to fix`}
                description={<><Problems problems={problems} onGo={goTo} /><Text type="secondary" style={{ fontSize: 12 }}>Until they are fixed this is an incomplete draft: it cannot run, not even as Exploratory. Your edits are still here; the saved version is unchanged.</Text></>} />
            )}
            <ContextSection draft={draft} original={original} onChange={setDraft} check={check} editable={editable} isDark={isDark}
              agentName={view.agentName ?? `agent #${view.agentId}`} onCreateVariant={() => setVariantOpen(true)} />
            <ActorsSection draft={draft} original={original} onChange={setDraft} check={check} editable={editable} isDark={isDark} agentName={view.agentName ?? `agent #${view.agentId}`} />
            <ExpectationsSection draft={draft} original={original} onChange={setDraft} check={check} editable={editable} isDark={isDark} />
            <ChecksSection draft={draft} original={original} onChange={setDraft} check={check} editable={editable} isDark={isDark} />
            <ControlsSection draft={draft} original={original} onChange={setDraft} check={check} editable={editable} isDark={isDark} />
            <ToolAnswersSection draft={draft} original={original} onChange={setDraft} check={check} editable={editable} isDark={isDark} />
            <HazardSourcesSection draft={draft} original={original} onChange={setDraft} check={check} editable={editable} isDark={isDark} />
            <SiblingsSection draft={draft} original={original} onChange={setDraft} check={check} editable={editable} isDark={isDark} />
            <ProvenanceSection draft={draft} original={original} onChange={setDraft} check={check} editable={editable} isDark={isDark}
              versions={history?.versions ?? []} decisions={history?.decisions ?? []} currentNumber={currentNumber} dirty={dirty} historyError={historyError} />
          </div>
        </>
      )}

      {conflict && draft && original && (
        <ScenarioMergeDrawer open={mergeOpen} onClose={() => setMergeOpen(false)} original={original} yours={draft} current={conflict.current}
          savedBy={savedByName} onUse={applyMerged} />
      )}
      {draft && original && (
        <StandaloneVariantDrawer open={variantOpen} onClose={() => setVariantOpen(false)} draft={draft} baseHash={baseHash}
          originalProtocolId={original.world.infusionProtocolId} originalNumber={currentNumber}
          onCreated={(id) => {
            setVariantOpen(false);
            onSaved?.();
            void message.success(`Created ${id}. The original is kept as it is.`);
            if (onOpenScenario) onOpenScenario(id);
          }} />
      )}
    </Drawer>
  );
});

/** The compiler's word on the draft: whether it runs, and whether it can be approved. */
function CompileStatus({ check, checkedAt, drafts, isDark }: { check: ScenarioVersionCheck | null; checkedAt: Date | null; drafts: number; isDark: boolean }) {
  if (!check) return null;
  const ok = check.runnable;
  return (
    <div style={{ marginTop: 10, padding: 10, borderRadius: 8, fontSize: 12.5, background: ok ? (isDark ? 'rgba(5,150,105,.14)' : colors.green[100]) : (isDark ? 'rgba(220,38,38,.14)' : colors.red[100]) }} aria-label="Compile status">
      <div style={{ fontWeight: 600 }}>
        {ok ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />} {ok ? 'Compiles' : `Can’t run: ${check.problems.length} to fix`}
        {checkedAt && <span style={{ fontWeight: 400 }}> · checked {checkedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>}
      </div>
      {ok && <div>Runnable draft: runs as Exploratory until approved</div>}
      {check.notApplicableCheckIds.length > 0 && <div>{check.notApplicableCheckIds.length} check(s) Not applicable without a protocol</div>}
      {(check.controls ?? []).length > 0 && (
        <div>
          {check.controls!.filter((v) => v.behaves).length} of {check.controls!.length} control(s) behave
          {check.controls!.some((v) => !v.behaves) ? ': a warning, not a block' : ''}
        </div>
      )}
      <div>{drafts > 0 ? `Approval: blocked, ${drafts} clinical draft(s)` : ok ? 'Approval: a reviewer may approve it once saved' : 'Approval: not until it runs'}</div>
    </div>
  );
}

/** The server's refusals, in its words, each with a way to the check it names. */
function Problems({ problems, onGo }: { problems: string[]; onGo: (id: string) => void }) {
  return (
    <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
      {problems.map((p) => {
        const id = /check '([^']+)'/i.exec(p)?.[1];
        const turn = /Turn (\d+) says nothing/.exec(p);
        // Version 3's parts, by the words the compiler uses for them.
        const part = /^Control |controls share/.test(p) ? 'controls' : /double/.test(p) ? 'doubles' : /^A source/.test(p) ? 'basis'
          : /relation|own sibling/i.test(p) ? 'siblings' : /names a role/.test(p) ? 'context' : null;
        return (
          <li key={p}>
            {p}{' '}
            {id && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onGo(`check-${id}`)}>Go to the check</Button>}
            {turn && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onGo('section-actors')}>Go to the turn</Button>}
            {/protocol/i.test(p) && !id && !part && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onGo('section-context')}>Go to Context</Button>}
            {part && !id && (
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onGo(`section-${part}`)}>
                Go to {SECTIONS.find((x) => x.key === part)?.label}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
